import { Button, StickyBarContent, StickyBarWrapper } from '@common/components';
import { SectionWrapper, Heading } from '@common/components/BookingForms';
import { SpaEntityAddExistingGuestsV2 } from '@common/components/SpaEntity/components/SpaEntityAddExistingGuestsV2';
import { SpaEntityGuestFieldV2 } from '@common/components/SpaEntity/components/SpaEntityGuestFieldV2';
import { useLabels } from '@features/Menu/useLabels';
import { useTranslations } from '@lang';
import { guestSelectors } from '@store/guest/selectors';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Container } from 'styled-bootstrap-grid';
import { Guest } from 'shared-types/Basket';
import { Discount, PriceWithPrefixAndSuffix } from 'shared-types';
import { SpaClass } from '../SpaClasses.types';
import { SpaClassFormValues } from '../SpaClassForm.schema';

type SpaClassesGuestDetailsStepV2Props = {
  existingGuests: Guest[];
  totalGuestAmount: number;
  isGenderSpecific: string;
  spaClass: SpaClass;
  timeSlotPrice: PriceWithPrefixAndSuffix;
  discount?: { discountedPrice: PriceWithPrefixAndSuffix } | null;
  showGenderNeutral: boolean;
  onSubmit: (data: SpaClassFormValues) => void;
};

export const SpaClassesGuestDetailsStepV2 = ({
  existingGuests,
  totalGuestAmount,
  isGenderSpecific,
  spaClass,
  timeSlotPrice,
  discount,
  showGenderNeutral,
  onSubmit,
}: SpaClassesGuestDetailsStepV2Props) => {
  const { t } = useTranslations();
  const labels = useLabels();
  const { control, handleSubmit, formState } = useFormContext<SpaClassFormValues>();
  const fieldArray = useFieldArray({ control, name: 'guests' });
  const guestValues = useWatch({ control, name: 'guests' }) ?? [];
  const loggedGuest = useSelector(guestSelectors.getGuest) as Guest | null;

  const selectedGuests = guestValues as Guest[];

  const displayedPrice =
    guestValues.length > 1
      ? { ...timeSlotPrice, amount: guestValues.length * timeSlotPrice.amount }
      : timeSlotPrice;

  const discountedPrice: Discount | null | undefined =
    guestValues.length > 1
      ? ({
          discountedPrice: {
            ...discount?.discountedPrice,
            amount: guestValues.length * (discount?.discountedPrice?.amount ?? 0),
          },
        } as Discount)
      : discount;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Container fluid>
        <Heading>{t('common.guestDetails')}</Heading>
        <SectionWrapper>
          {t('common.classGuestAmountDescription', { guestAmount: totalGuestAmount })}
          <SpaEntityAddExistingGuestsV2
            fieldArray={fieldArray as any}
            guestValues={guestValues as any}
            selectedGuests={selectedGuests}
            existingGuests={existingGuests}
            guest={loggedGuest}
            minimumGuests={1}
            maximumGuests={totalGuestAmount}
            isGenderSpecific={isGenderSpecific}
          />
          <SpaEntityGuestFieldV2
            fieldArray={fieldArray as any}
            minimumGuests={1}
            maximumGuests={totalGuestAmount}
            isGenderSpecific={isGenderSpecific}
            showGenderNeutral={showGenderNeutral}
            defaultGender={isGenderSpecific || ''}
          />
        </SectionWrapper>
      </Container>
      <StickyBarWrapper>
        <StickyBarContent
          name={spaClass.name}
          subtitle={labels?.classes || t('common.classes')}
          displayedPrice={displayedPrice}
          discountedPrice={discountedPrice}
          button={
            <Button isPrimary type="submit" disabled={!formState.isValid || formState.isSubmitting}>
              {t('roomPage.bookRoomCTA')}
            </Button>
          }
        />
      </StickyBarWrapper>
    </form>
  );
};
